import React, { Fragment, ReactNode } from 'react';
import { connect } from 'react-redux';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import has from 'lodash/has';

import { v4 } from 'uuid';
import {
  NavLinkOverride, ExternalLink,
} from './styles';
import { SeparatorOverride } from '../../../Shared/styles/styles';
import { Routes, Colors } from '../../../../constants';
import { UiSection } from '../../../../enums';
import { VaultButton } from '../../../Shared';
import { IAppState } from '../../../../state/reducers';
import { IProps, IStateProps, IDispatchProps } from './interfaces';
import { Product } from '../../../../state/product';
import { toggleContactInfoDialog } from '../../../../state/exchange/contactInfoDialog';
import {
  toggleUpdateGameScheduleDialog,
} from '../../../../state/exchange/updateGameScheduleDialog';
import {
  toggleCreateGameScheduleDialog,
} from '../../../../state/exchange/createGameScheduleDialog';
import {
  KeyRoleUserAccessPermission,
  selectIsUserVaultSuperAdmin,
  selectUserOrgsSecurityProfiles,
} from '../../../../state/user/userOrgRole';
import ContactInfoDialog from '../../../Exchange/ContactInfoDialog/component';
import UpdateGameScheduleDialog from '../../../Exchange/UpdateGameScheduleDialog/component';
import CreateGameScheduleDialog from '../../../Exchange/CreateGameScheduleDialog/component';
import { toggleStorageSummary } from '../../../../state/storageSummary';
import StorageSummaryDialog from '../../../Storage/StorageSummaryDialog/storageSummaryDialog';
import ActiveUsersDialog from '../../../Exchange/ActiveUsersDialog/component';
import { toggleActiveUsersDialog } from '../../../../state/exchange/activeUsers';

const ToolsMenu: React.FC<IProps> = (props: IProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const adminMenuID: string = `${UiSection.Header}.Menu.Admin`;
  const adminButtonID: string = `${UiSection.Header}.Button.Admin`;

  const manageLiveEventLinkID = `${UiSection.Header}.Menu.Admin.Item.ManageLiveEvents`;
  const publishProfilesLinkID = `${UiSection.Header}.Menu.Admin.Item.PublishProfiles`;
  const adminDashboardLinkID = `${UiSection.Header}.Menu.Admin.Item.Dashboard`;
  const idmLinkID = `${UiSection.Header}.Menu.Admin.Item.Idm`;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    userOrgRoleState,
    isExchange,
    isSuperAdmin,
    securityProfiles,
    toggleActiveUsersDialog,
    onToggleContactInfoDialog,
    onToggleUpdateGameSchedule,
    onToggleCreateGame,
    onToggleStorageSummary,
  } = props;
  const { loginAs = null, useAs = null } = userOrgRoleState || {};
  const role = useAs || loginAs;
  const hasManageUsersOrgPermission = role?.userAccessPermission?.manageUsersOrg ?? false;
  const hasManageLiveVideoPermission = role?.userAccessPermission?.addLiveVideo ?? false;
  const hasPublishProfilePermission = role?.userAccessPermission?.publishProfile ?? false;
  const hasContactInfoPermission = role?.userAccessPermission?.contactInfo ?? false;
  const hasTeamId = securityProfiles.some(({ team_id }) => !!team_id);

  // TODO: i18n

  const menuChildren: ReactNode[] = [];
  if (isSuperAdmin) {
    menuChildren.push(
      <ExternalLink id={adminDashboardLinkID} href={Routes.ADMIN_DASHBOARD} key={v4()}>
        <MenuItem onClick={handleClose}>Admin Dashboard</MenuItem>
      </ExternalLink>,
    );
  }

  if (isExchange) {
    menuChildren.push(<SeparatorOverride horizontal color={Colors.BLACK_20} key={v4()} />);
    menuChildren.push(
      <MenuItem
        onClick={() => {
          handleClose();
          toggleActiveUsersDialog();
        }}
        data-auto="ContactInfo"
        key={v4()}
      >
        Active Users
      </MenuItem>,
    );
    if (hasContactInfoPermission && hasTeamId) {
      menuChildren.push(
        <MenuItem
          onClick={() => {
            handleClose();
            onToggleContactInfoDialog();
          }}
          data-auto="ContactInfo"
          key={v4()}
        >Contact Info
        </MenuItem>,
      );
    }
    if (isSuperAdmin) {
      menuChildren.push(
        <MenuItem
          onClick={() => {
            handleClose();
            onToggleUpdateGameSchedule();
          }}
          data-auto="GameSchedule"
          key={v4()}
        >Update Schedule
        </MenuItem>,
      );
      menuChildren.push(
        <MenuItem
          onClick={() => {
            handleClose();
            onToggleCreateGame();
          }}
          data-auto="CreateGame"
          key={v4()}
        >Create Game
        </MenuItem>,
      );
    }
  }

  if (hasManageLiveVideoPermission || hasPublishProfilePermission) {
    menuChildren.push(<SeparatorOverride horizontal color={Colors.BLACK_20} key={v4()} />);
  }
  if (hasManageLiveVideoPermission) {
    menuChildren.push(
      <ExternalLink id={manageLiveEventLinkID} href={Routes.MANAGE_LIVE_EVENTS} target="_blank" key={v4()}>
        <MenuItem onClick={handleClose}>Manage Live Events</MenuItem>
      </ExternalLink>,
    );
  }
  if (hasPublishProfilePermission) {
    menuChildren.push(
      <NavLinkOverride id={publishProfilesLinkID} to={Routes.PUBLISH_PROFILES} key={v4()}>
        <MenuItem onClick={handleClose}>Publish Profiles</MenuItem>
      </NavLinkOverride>,
    );
  }

  if (hasManageUsersOrgPermission) {
    menuChildren.push(<SeparatorOverride horizontal color={Colors.BLACK_20} key={v4()} />);
    menuChildren.push(
      <ExternalLink
        id={idmLinkID}
        href={`${process.env.REACT_APP_IDM_CLIENT_URL}/orgs?branding=xos`}
        target="_blank"
        key={v4()}
      >
        <MenuItem>Manage Orgs/Users</MenuItem>
      </ExternalLink>,
    );
  }

  // make storage summary condition to bound this case
  menuChildren.push(
    <MenuItem
      onClick={() => {
        handleClose();
        onToggleStorageSummary();
      }}
      data-auto="StorageSummaryButton"
      key={v4()}
    >Storage
    </MenuItem>,
  );

  return (
    <>
      <VaultButton
        id={adminButtonID}
        aria-owns={anchorEl ? 'simple-menu' : undefined}
        variant="transparentOnBlack"
        aria-haspopup="true"
        onClick={handleClick}
      >
        Tools
      </VaultButton>
      <ContactInfoDialog />
      <UpdateGameScheduleDialog />
      <CreateGameScheduleDialog />
      <StorageSummaryDialog />
      <ActiveUsersDialog />
      <Menu
        id={adminMenuID}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!anchorEl}
        onClose={handleClose}
      >{menuChildren}
      </Menu>
    </>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  userOrgRoleState: state.user.userOrgRole,
  isExchange: state.product.target === Product.Exchange,
  isSuperAdmin: selectIsUserVaultSuperAdmin(state),
  securityProfiles: selectUserOrgsSecurityProfiles(state),
});

const mapDispatchToProps: IDispatchProps = {
  toggleActiveUsersDialog,
  onToggleContactInfoDialog: toggleContactInfoDialog,
  onToggleUpdateGameSchedule: toggleUpdateGameScheduleDialog,
  onToggleCreateGame: toggleCreateGameScheduleDialog,
  onToggleStorageSummary: toggleStorageSummary,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ToolsMenu);
